// HTTP client targeted at the admin-api OData handler exposed at
// /api/data-api/rest/... — SWA's `/api/*` route rewrites to the linked admin-api
// Functions app, forwarding the client principal via x-ms-client-principal
// automatically. No bearer token, no x-functions-key. Response envelope:
// `{ value: T[]; nextLink? }` for lists, `{ value: [row] }` for items.

import { fetchPrincipal, invalidateSession, signInUrl } from '@/scripts/auth';

const BASE = '/api/data-api/rest';

let sessionExpiredRedirecting = false;

function redirectToSignIn(): void {
  if (sessionExpiredRedirecting) return;
  sessionExpiredRedirecting = true;
  invalidateSession();
  window.location.href = signInUrl(window.location.pathname + window.location.search);
}

async function checkSessionExpiry(): Promise<void> {
  if (sessionExpiredRedirecting) return;
  const principal = await fetchPrincipal(true);
  if (!principal) redirectToSignIn();
}

// SQL NULL travels as `null`. `JSON.stringify` drops properties whose value is `undefined`, and
// the OData handler builds its `SET` list from the keys present in the body — so a dropped key
// leaves the column at its old value and still answers 200, which reads as a successful save.
// Normalising here, the single point where a body is serialised, means no call site has to
// remember. A request that genuinely wants a column left alone omits the key entirely; absent
// keys stay absent, so partial PATCH semantics are unaffected.
function undefinedToNull(_key: string, value: unknown): unknown {
  return value === undefined ? null : value;
}

// A write body should never carry `undefined` — it means a clear handler emitted `undefined`
// instead of `null`, and the replacer above is masking a bug at the source. Name it in dev so it
// gets fixed there rather than silently corrected on every request.
function warnOnUndefined(data: object): void {
  if (import.meta.env.PROD) return;
  const keys = Object.entries(data)
    .filter(([, v]) => v === undefined)
    .map(([k]) => k);
  if (keys.length > 0) {
    console.warn(
      `OData write body carries undefined for: ${keys.join(', ')}. ` +
        `Coerced to null — fix the source to emit null directly.`,
    );
  }
}

async function BaseRequest(data?: object | FormData): Promise<RequestInit> {
  const isFormData = data instanceof FormData;
  if (data && !isFormData) warnOnUndefined(data);
  const body = !data ? {} : { body: isFormData ? data : JSON.stringify(data, undefinedToNull) };
  const headers: HeadersInit = isFormData
    ? {}
    : {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
  return {
    headers,
    credentials: 'include' as const,
    ...body,
  };
}

async function HandleResponse(response: Response): Promise<unknown> {
  // fetch follows redirects silently. SWA's responseOverrides rewrites any 401
  // on /api/* into a 302 to /.auth/login/aad — a redirected final response is
  // an unambiguous auth failure. Redirect to sign-in immediately (no /.auth/me
  // re-check needed; the SWA config makes this decisive).
  if (response.redirected) {
    redirectToSignIn();
    throw new Error('Session expired — redirecting to sign in');
  }
  if (response.status === 204) return null;
  if (!response.ok) throw new Error(response.statusText || `HTTP ${response.status}`);
  const text = await response.text();
  if (!text) return null;
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('json')) {
    throw new Error(`Expected JSON, got ${contentType || 'unknown content-type'}`);
  }
  return JSON.parse(text);
}

function QueryString(value?: string | { [key: string]: unknown }): string {
  if (!value) return '';
  if (typeof value === 'string') return `?${value}`;
  const str: string[] = [];
  for (const p in value) {
    if (Object.prototype.hasOwnProperty.call(value, p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(String(value[p])));
    }
  }
  return str.length ? `?${str.join('&')}` : '';
}

// In production, SWA's 401→302 can chain to a cross-origin redirect
// (login.microsoftonline.com), causing fetch to throw a TypeError before a
// response object is produced. Catch it here, verify with /.auth/me (same-origin,
// CORS-safe), and redirect to sign-in only when the session is genuinely gone.
async function request(input: string, init: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(input, init);
  } catch (e) {
    await checkSessionExpiry();
    throw e;
  }
  return HandleResponse(response);
}

export async function Get(
  url: string,
  query?: string | { [key: string]: unknown },
): Promise<unknown> {
  return request(BASE + url + QueryString(query), {
    method: 'GET',
    ...(await BaseRequest()),
  });
}

export async function Post(url: string, data?: object | FormData): Promise<unknown> {
  return request(BASE + url, {
    method: 'POST',
    ...(await BaseRequest(data)),
  });
}

export async function Patch(url: string, data?: object | FormData): Promise<unknown> {
  return request(BASE + url, {
    method: 'PATCH',
    ...(await BaseRequest(data)),
  });
}

export async function Put(url: string, data?: object | FormData): Promise<unknown> {
  return request(BASE + url, {
    method: 'PUT',
    ...(await BaseRequest(data)),
  });
}

export async function Delete(url: string, data?: object | FormData): Promise<unknown> {
  return request(BASE + url, {
    method: 'DELETE',
    ...(await BaseRequest(data)),
  });
}

// OData REST list response envelope. `value` contains the page of rows; `nextLink`
// is the continuation URL for cursor pagination (absent on the final page).
export interface ListResponse<T> {
  value: T[];
  nextLink?: string;
}
